import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Checkbox, Dialog, DialogDescription, DialogHeader, DialogTitle } from 'components';
import { useRecordObjection } from 'hooks/useRecordObjection';
import { RecordingObjectionScope } from 'types/recordingObjection';
import { getApiErrorMessage } from 'utils/apiError';
import {
  CancelButton,
  ObjectionDialogContent,
  ObjectionDialogFooter,
  ObjectionOptionBox,
  ObjectionOptionHint,
  ObjectionOptionRow,
  SubmitObjectionButton,
} from './RecordingObjectionDialog.styles';

interface RecordingObjectionDialogProps {
  medatixxPatientId: string | undefined;
  onClose: () => void;
}

export const RecordingObjectionDialog = ({
  medatixxPatientId,
  onClose,
}: RecordingObjectionDialogProps) => {
  const { t } = useTranslation();
  const [isPermanent, setIsPermanent] = useState(false);
  const { mutate: recordObjection, isPending } = useRecordObjection(medatixxPatientId);

  const submit = () => {
    recordObjection(
      isPermanent ? RecordingObjectionScope.Permanent : RecordingObjectionScope.Today,
      {
        onSuccess: onClose,
        onError: (error) => toast.error(getApiErrorMessage(error, t)),
      }
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <ObjectionDialogContent data-ph-no-autocapture>
        <DialogHeader>
          <DialogTitle>{t('recording.objection.title')}</DialogTitle>
          <DialogDescription>{t('recording.objection.description')}</DialogDescription>
        </DialogHeader>
        <ObjectionOptionBox>
          <ObjectionOptionRow>
            <Checkbox
              checked={isPermanent}
              onCheckedChange={(checked) => setIsPermanent(checked === true)}
              label={t('recording.objection.permanentLabel')}
            />
          </ObjectionOptionRow>
          <ObjectionOptionHint>{t('recording.objection.permanentHint')}</ObjectionOptionHint>
        </ObjectionOptionBox>
        <ObjectionDialogFooter>
          <CancelButton onClick={onClose}>{t('cancel')}</CancelButton>
          <SubmitObjectionButton onClick={submit} disabled={isPending}>
            {t('recording.objection.submit')}
          </SubmitObjectionButton>
        </ObjectionDialogFooter>
      </ObjectionDialogContent>
    </Dialog>
  );
};
